import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, CheckCircle, AlertTriangle, XCircle, RefreshCw,
  Wifi, Brain, BarChart3, TrendingUp, Target, Layers,
  Clock, Zap, Shield, Eye
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ExpiryCheckPanel from './ExpiryCheckPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  active:          { color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: CheckCircle, label: 'Active' },
  connected:       { color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: Wifi, label: 'Connected' },
  idle:            { color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: Clock, label: 'Idle' },
  inactive:        { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: XCircle, label: 'Inactive' },
  not_initialized: { color: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/30', icon: XCircle, label: 'Not Init' },
  no_data:         { color: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/30', icon: AlertTriangle, label: 'No Data' },
  disconnected:    { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: XCircle, label: 'Disconnected' },
  error:           { color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: AlertTriangle, label: 'Error' },
};

const ENGINE_ICONS = {
  hft_engine_nifty: BarChart3,
  hft_engine_banknifty: BarChart3,
  god_score_nifty: Brain,
  god_score_banknifty: Brain,
  iv_skew_analyzer: Zap,
  footprint_builder: Layers,
  tpo_profile_nifty: Target,
  tpo_profile_banknifty: Target,
  gex_engine_nifty: TrendingUp,
  gex_engine_banknifty: TrendingUp,
  oi_tracker: Eye,
  websocket_feed: Wifi,
};

function formatTimestamp(ts) {
  if (!ts || ts === 0) return 'Never';
  const d = new Date(typeof ts === 'number' ? (ts > 1e12 ? ts : ts * 1000) : ts);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

function formatVolume(val) {
  if (!val) return '0';
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)}Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
  return val.toLocaleString();
}

// Engine Health Card
const EngineCard = ({ engineKey, engine }) => {
  const statusCfg = STATUS_CONFIG[engine.status] || STATUS_CONFIG.error;
  const StatusIcon = statusCfg.icon;
  const EngineIcon = ENGINE_ICONS[engineKey] || Activity;

  return (
    <div className={`rounded-lg border ${statusCfg.border} ${statusCfg.bg} p-3`} data-testid={`engine-card-${engineKey}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <EngineIcon className={`w-4 h-4 ${statusCfg.color}`} />
          <span className="text-sm font-medium text-white">{engine.name}</span>
        </div>
        <Badge variant="outline" className={`text-[10px] ${statusCfg.color} border-current`}>
          <StatusIcon className="w-3 h-3 mr-1" />
          {statusCfg.label}
        </Badge>
      </div>
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>{engine.events?.toLocaleString() || 0} {engine.event_label || 'events'}</span>
        <span>Last: {formatTimestamp(engine.last_active)}</span>
      </div>
      {engine.details && Object.keys(engine.details).length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/5 grid grid-cols-2 gap-1 text-[10px]">
          {Object.entries(engine.details).slice(0, 6).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-1">
              <span className="text-gray-500 truncate">{k.replace(/_/g, ' ')}:</span>
              <span className={`font-mono ${typeof v === 'boolean' ? (v ? 'text-green-400' : 'text-red-400') : 'text-gray-300'}`}>
                {typeof v === 'boolean' ? (v ? 'Yes' : 'No') 
                  : typeof v === 'number' ? v.toLocaleString() 
                  : typeof v === 'object' ? JSON.stringify(v) 
                  : String(v || '-')}
              </span>
            </div>
          ))}
        </div>
      )}
      {engine.error && (
        <div className="mt-2 text-[10px] text-red-400 truncate">{engine.error}</div>
      )}
    </div>
  );
};

// Health Summary Bar
const HealthSummary = ({ summary }) => {
  if (!summary) return null;
  const healthColor = summary.health_pct >= 70 ? 'text-green-400' : summary.health_pct >= 40 ? 'text-yellow-400' : 'text-red-400';
  const healthBg = summary.health_pct >= 70 ? 'bg-green-500' : summary.health_pct >= 40 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className="rounded-lg border border-border bg-surface p-4 mb-4" data-testid="health-summary">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield className={`w-5 h-5 ${healthColor}`} />
          <span className="text-lg font-bold text-white">System Health</span>
        </div>
        <span className={`text-2xl font-bold font-mono ${healthColor}`}>{summary.health_pct}%</span>
      </div>
      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-800 rounded-full mb-3">
        <div className={`h-full rounded-full ${healthBg} transition-all duration-500`} style={{ width: `${summary.health_pct}%` }} />
      </div>
      <div className="grid grid-cols-5 gap-2 text-xs text-center">
        <div><span className="text-green-400 font-bold text-lg">{summary.active}</span><br /><span className="text-gray-500">Active</span></div>
        <div><span className="text-yellow-400 font-bold text-lg">{summary.idle}</span><br /><span className="text-gray-500">Idle</span></div>
        <div><span className="text-red-400 font-bold text-lg">{summary.inactive}</span><br /><span className="text-gray-500">Inactive</span></div>
        <div><span className="text-red-500 font-bold text-lg">{summary.errors}</span><br /><span className="text-gray-500">Errors</span></div>
        <div><span className="text-gray-400 font-bold text-lg">{summary.total_engines}</span><br /><span className="text-gray-500">Total</span></div>
      </div>
    </div>
  );
};

// Volume Validation Section (NIFTY + BANKNIFTY)
const VolumeCard = ({ symbol, data }) => {
  if (!data) return null;
  return (
    <div className="mb-3" data-testid={`volume-card-${symbol.toLowerCase()}`}>
      <div className="text-[10px] text-gray-500 font-semibold mb-1.5 uppercase">{symbol}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-800/50 rounded p-2 text-center">
          <div className="text-[10px] text-gray-500">Session Volume</div>
          <div className="text-sm font-mono text-white">{formatVolume(data.session_volume_shares)}</div>
          <div className="text-[10px] text-gray-500">{data.session_volume_lots?.toLocaleString() || 0} lots</div>
        </div>
        <div className="bg-gray-800/50 rounded p-2 text-center">
          <div className="text-[10px] text-gray-500">CVD</div>
          <div className={`text-sm font-mono ${(data.cvd || 0) < 0 ? 'text-red-400' : 'text-green-400'}`}>
            {data.cvd?.toLocaleString() || 0}
          </div>
          <div className="text-[10px] text-gray-500">Agg: {data.aggression_pct || 0}%</div>
        </div>
        <div className="bg-gray-800/50 rounded p-2 text-center">
          <div className="text-[10px] text-gray-500">Buy / Sell</div>
          <div className="text-sm font-mono">
            <span className="text-green-400">{formatVolume(data.buy_volume)}</span>
            <span className="text-gray-600"> / </span>
            <span className="text-red-400">{formatVolume(data.sell_volume)}</span>
          </div>
          <div className={`text-[10px] ${(data.sell_bias_pct || 50) > 52 ? 'text-red-400' : 'text-gray-500'}`}>
            Sell: {data.sell_bias_pct || 50}%
          </div>
        </div>
        <div className="bg-gray-800/50 rounded p-2 text-center">
          <div className="text-[10px] text-gray-500">Lot Size</div>
          <div className="text-sm font-mono text-white">{data.lot_size || '-'}</div>
          <div className="text-[10px] text-gray-500">Qty per lot</div>
        </div>
      </div>
    </div>
  );
};

const VolumeValidation = ({ data, reference, onSaveReference }) => {
  const [refCvd, setRefCvd] = useState('');
  const [refVol, setRefVol] = useState('');
  const [refNotes, setRefNotes] = useState('');
  const nifty = data?.nifty || {};
  const banknifty = data?.banknifty || {};

  return (
    <div className="rounded-lg border border-border bg-surface p-4 mb-4" data-testid="volume-validation">
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-blue-400" />
        Volume & CVD Validation
      </h3>

      <VolumeCard symbol="NIFTY" data={nifty} />
      <VolumeCard symbol="BANKNIFTY" data={banknifty} />

      {/* Reference Input */}
      {reference && reference.gocharting_cvd && (
        <div className="bg-blue-900/20 border border-blue-500/20 rounded p-2 mb-3">
          <div className="text-[10px] text-blue-400 mb-1">GoCharting Reference</div>
          <div className="flex gap-4 text-xs">
            <span>CVD: <span className="text-white font-mono">{reference.gocharting_cvd?.toLocaleString()}</span></span>
            <span>Vol: <span className="text-white font-mono">{reference.gocharting_volume?.toLocaleString()}</span></span>
            {nifty.cvd && reference.gocharting_cvd && (
              <span>Deviation: <span className={`font-mono ${Math.abs(1 - nifty.cvd / reference.gocharting_cvd) > 0.3 ? 'text-red-400' : 'text-green-400'}`}>
                {((nifty.cvd / reference.gocharting_cvd - 1) * 100).toFixed(1)}%
              </span></span>
            )}
          </div>
        </div>
      )}
      
      <details className="text-xs">
        <summary className="text-gray-500 cursor-pointer hover:text-gray-300">Enter GoCharting Reference Data</summary>
        <div className="mt-2 flex gap-2 items-end flex-wrap">
          <div>
            <label className="text-gray-500 text-[10px]">GoCharting CVD</label>
            <input type="number" value={refCvd} onChange={e => setRefCvd(e.target.value)}
              className="block w-28 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs" placeholder="-1060000" />
          </div>
          <div>
            <label className="text-gray-500 text-[10px]">GoCharting Volume</label>
            <input type="number" value={refVol} onChange={e => setRefVol(e.target.value)}
              className="block w-28 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs" placeholder="7960000" />
          </div>
          <div>
            <label className="text-gray-500 text-[10px]">Notes</label>
            <input type="text" value={refNotes} onChange={e => setRefNotes(e.target.value)}
              className="block w-40 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs" placeholder="Strong sell day" />
          </div>
          <Button size="sm" variant="outline" className="text-xs h-7"
            onClick={() => {
              if (refCvd || refVol) {
                onSaveReference({ gocharting_cvd: Number(refCvd) || null, gocharting_volume: Number(refVol) || null, notes: refNotes });
                setRefCvd(''); setRefVol(''); setRefNotes('');
              }
            }}>Save</Button>
        </div>
      </details>
    </div>
  );
};

// Signal Summary Section  
const SignalSummary = ({ data }) => {
  if (!data || data.error) return null;
  const breakdown = data.breakdown || {};

  return (
    <div className="rounded-lg border border-border bg-surface p-4 mb-4" data-testid="signal-summary">
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <Target className="w-4 h-4 text-purple-400" />
        Signal Summary
      </h3>
      <div className="grid grid-cols-3 gap-3 mb-2">
        <div className="bg-gray-800/50 rounded p-2 text-center">
          <div className="text-lg font-bold text-white">{data.total_actionable}</div>
          <div className="text-[10px] text-gray-500">Signals Fired</div>
        </div>
        <div className="bg-gray-800/50 rounded p-2 text-center">
          <div className="text-lg font-bold text-yellow-400">{data.total_blocked}</div>
          <div className="text-[10px] text-gray-500">Blocked (Vetoed)</div>
        </div>
        <div className="bg-gray-800/50 rounded p-2 text-center">
          <div className="text-lg font-bold text-blue-400">
            {data.total_actionable + data.total_blocked > 0
              ? `${Math.round(data.total_blocked / (data.total_actionable + data.total_blocked) * 100)}%`
              : '-'}
          </div>
          <div className="text-[10px] text-gray-500">Block Rate</div>
        </div>
      </div>
      {Object.entries(breakdown).map(([symbol, b]) => (
        <div key={symbol} className="flex items-center gap-3 text-xs text-gray-400 mt-1">
          <span className="font-mono text-white w-20">{symbol}</span>
          <span className="text-green-400">{b.longs}L</span>
          <span className="text-red-400">{b.shorts}S</span>
          <span className="text-yellow-400">{b.blocked} blocked</span>
        </div>
      ))}
    </div>
  );
};

// Main Component
const AlgoHealthTab = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reference, setReference] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/algo-health`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReport(data);

      // Also fetch reference data for today
      const today = new Date().toISOString().split('T')[0];
      try {
        const refRes = await fetch(`${API}/admin/algo-health/report/${today}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (refRes.ok) {
          const refData = await refRes.json();
          setReference(refData.reference || null);
        }
      } catch { /* ignore */ }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const saveReference = async (refData) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/admin/algo-health/reference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(refData),
      });
      fetchReport();
    } catch (err) {
      console.error('Failed to save reference:', err);
    }
  };

  if (error) {
    return (
      <div className="p-4 text-red-400">
        <AlertTriangle className="w-5 h-5 inline mr-2" />
        Failed to load health report: {error}
        <Button size="sm" variant="outline" className="ml-3" onClick={fetchReport}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="algo-health-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Activity className="w-5 h-5 text-cyan-400" />
          Algo Health Monitor
        </h2>
        <div className="flex items-center gap-2">
          {report?.generated_at && (
            <span className="text-[10px] text-gray-500">
              Generated: {new Date(report.generated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}
            </span>
          )}
          <Button size="sm" variant="outline" onClick={fetchReport} disabled={loading} data-testid="refresh-health">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {report && (
        <>
          {/* Health Summary */}
          <HealthSummary summary={report.summary} />

          {/* Expiry Check & Holiday Calendar */}
          <ExpiryCheckPanel />

          {/* Engine Grid */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-2">Engine Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(report.engines || {}).map(([key, engine]) => (
                <EngineCard key={key} engineKey={key} engine={engine} />
              ))}
            </div>
          </div>

          {/* Volume Validation */}
          <VolumeValidation
            data={report.volume_validation}
            reference={reference}
            onSaveReference={saveReference}
          />

          {/* Signal Summary */}
          <SignalSummary data={report.signal_summary} />
        </>
      )}
    </div>
  );
};

export default AlgoHealthTab;
